import { playerById, techniqueById } from './data';
import type { MatchEvent } from './types';

export type MatchNarration = { text: string; emphasis: string[] };

type NarrationContext = {
  actor: string;
  target: string;
  technique: string;
  teamName: string;
};

const aliasFor = (id?: string) => id ? playerById.get(id)?.metadata?.nickname?.trim() || playerById.get(id)?.name.split(' ')[0] || 'el jugador' : 'el jugador';
const techniqueFor = (event: MatchEvent) => event.techniqueId ? techniqueById.get(event.techniqueId)?.name ?? '' : '';

function contextFor(event: MatchEvent): NarrationContext {
  const targetFromMessage = event.message.match(/conecta con ([^.!]+)/i)?.[1]?.trim();
  const furyTeam = event.message.replace(/^La Fase de Furor de /i, '').replace(/ entra en Fase de Furor.*$/i, '').replace(/ llega a su fin.*$/i, '').trim();
  return {
    // The runtime slot is intentionally distinct from the catalogue player id.
    // Prefer the alias stored with the event: it also remains correct after a
    // substitution reuses the tactical slot.
    actor: event.actorAlias?.trim() || aliasFor(event.actorId),
    target: event.targetAlias?.trim() || (event.targetId ? aliasFor(event.targetId) : targetFromMessage?.split(' ')[0] || 'un compañero'),
    technique: techniqueFor(event),
    teamName: furyTeam,
  };
}

function indexFor(event: MatchEvent, salt: number, length: number) {
  const key = `${event.type}:${event.second}:${event.actorId}:${event.targetId ?? ''}:${event.techniqueId ?? ''}`;
  let hash = salt;
  for (let index = 0; index < key.length; index += 1) hash = (hash * 31 + key.charCodeAt(index)) >>> 0;
  return hash % length;
}

function choose(event: MatchEvent, salt: number, phrases: Array<(context: NarrationContext) => string>) {
  return phrases[indexFor(event, salt, phrases.length)](contextFor(event));
}

function techniqueSuffix(technique: string) {
  return technique ? ` con ${technique}` : '';
}

function narration(text: string, ...emphasis: Array<string | undefined>): MatchNarration {
  return { text, emphasis: [...new Set(emphasis.filter((value): value is string => Boolean(value)))].sort((a, b) => b.length - a.length) };
}

/** Brief, deterministic variants for the match feed. A technique is always
 * included whenever the engine attached its technique id to the action. */
export function matchLogNarration(event: MatchEvent): MatchNarration {
  const context = contextFor(event);
  switch (event.type) {
    case 'GOAL': {
      const text = choose(event, 11, [
        ({ actor, technique }) => `¡GOL! ${actor} define${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `¡GOL de ${actor}! Remate${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `¡A la red! ${actor} marca${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `¡GOL! ${actor} no perdona${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `¡GOLAZO! ${actor} culmina${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `¡GOL! ${actor} rompe el empate${techniqueSuffix(technique)}.`,
      ]);
      return narration(text, 'GOL', 'GOLAZO', context.actor, context.technique);
    }
    case 'SAVE': {
      const text = choose(event, 17, [
        ({ actor, technique }) => `${actor} firma una PARADA${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `¡PARADA de ${actor}! Responde${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `${actor} bloca el remate${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `${actor} salva a su equipo${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `Manos firmes de ${actor}${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `${actor} cierra la portería${techniqueSuffix(technique)}.`,
      ]);
      return narration(text, 'PARADA', context.actor, context.technique);
    }
    case 'TECHNIQUE': {
      const text = choose(event, 23, [
        ({ actor, technique }) => `${actor} activa ${technique}.`,
        ({ actor, technique }) => `${technique}: ${actor} la pone en marcha.`,
        ({ actor, technique }) => `${actor} desata ${technique}.`,
        ({ actor, technique }) => `¡${technique}! ${actor} eleva el ritmo.`,
        ({ actor, technique }) => `${actor} recurre a ${technique}.`,
        ({ actor, technique }) => `${technique} entra en juego con ${actor}.`,
      ]);
      return narration(text, context.actor, context.technique);
    }
    case 'PASS': {
      const text = choose(event, 29, [
        ({ actor, target, technique }) => `${actor} encuentra a ${target}${techniqueSuffix(technique)}.`,
        ({ actor, target, technique }) => `${actor} conecta con ${target}${techniqueSuffix(technique)}.`,
        ({ actor, target, technique }) => `Pase de ${actor} para ${target}${techniqueSuffix(technique)}.`,
        ({ actor, target, technique }) => `${actor} filtra hacia ${target}${techniqueSuffix(technique)}.`,
        ({ actor, target, technique }) => `${actor} habilita a ${target}${techniqueSuffix(technique)}.`,
      ]);
      return narration(text, context.actor, context.target, context.technique);
    }
    case 'DRIBBLE': {
      const text = choose(event, 31, [
        ({ actor, technique }) => `${actor} supera a su marca${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `${actor} rompe la línea${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `${actor} deja atrás al defensor${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `${actor} gana el duelo${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `${actor} se escapa${techniqueSuffix(technique)}.`,
      ]);
      return narration(text, context.actor, context.technique);
    }
    case 'TACKLE': {
      const text = choose(event, 37, [
        ({ actor, technique }) => `${actor} recupera el balón${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `${actor} corta la jugada${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `${actor} roba con autoridad${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `${actor} frena el avance${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `${actor} gana la posesión${techniqueSuffix(technique)}.`,
      ]);
      return narration(text, context.actor, context.technique);
    }
    case 'SHOT': {
      const text = choose(event, 41, [
        ({ actor, technique }) => `${actor} prueba el disparo${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `${actor} busca la escuadra${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `Remate de ${actor}${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `${actor} golpea con todo${techniqueSuffix(technique)}.`,
        ({ actor, technique }) => `${actor} apunta a portería${techniqueSuffix(technique)}.`,
      ]);
      return narration(text, context.actor, context.technique);
    }
    case 'REBOUND': return narration(`${context.actor} deja un REBOTE${techniqueSuffix(context.technique)}.`, 'REBOTE', context.actor, context.technique);
    case 'DEFLECTION': return narration(`${context.actor} desvía el balón${techniqueSuffix(context.technique)}.`, context.actor, context.technique);
    case 'SUBSTITUTION': return narration(`${context.actor} sale; entra ${context.target}.`, context.actor, context.target);
    case 'FURY': return event.message.includes('entra en')
      ? narration(`¡FASE DE FUROR! ${context.teamName} se enciende.`, 'FASE DE FUROR', context.teamName)
      : narration(`Termina la FASE DE FUROR de ${context.teamName}.`, 'FASE DE FUROR', context.teamName);
    case 'OUT': return narration('El balón se marcha fuera.');
    case 'THROW_IN': return narration(`Saque de banda para ${context.actor}.`, context.actor);
    case 'CORNER': return narration(`Córner provocado por ${context.actor}.`, 'Córner', context.actor);
    case 'GOAL_KICK': return narration('Saque de puerta.');
    case 'OFFSIDE': return narration('Fuera de juego señalado.', 'Fuera de juego');
    case 'FOUL': return narration(`Falta sobre ${context.target}.`, 'Falta', context.target);
    default: return narration(context.actor, context.actor);
  }
}

/** The commentator speaks only on broadcast-worthy moments. Its seed keeps a
 * replay consistent without repeating the same one or two lines. */
export function commentatorNarration(event: MatchEvent, commentatorId: string): MatchNarration {
  const context = contextFor(event);
  const saltedEvent = { ...event, actorId: `${event.actorId}:${commentatorId}` };
  if (event.type === 'GOAL') {
    const text = choose(saltedEvent, 61, [
      ({ actor, technique }) => `¡GOL de ${actor}${techniqueSuffix(technique)}! La grada explota.`,
      ({ actor, technique }) => `¡${actor} marca${techniqueSuffix(technique)}! Qué golpe.`,
      ({ actor, technique }) => `¡GOLAZO de ${actor}${techniqueSuffix(technique)}! Imparable.`,
      ({ actor, technique }) => `¡Aparece ${actor}${techniqueSuffix(technique)}! ¡GOL!`,
      ({ actor, technique }) => `¡${actor} sentencia${techniqueSuffix(technique)}! La red tiembla.`,
      ({ actor, technique }) => `¡GOL! ${actor} castiga${techniqueSuffix(technique)}.`,
      ({ actor, technique }) => `¡Qué final de ${actor}${techniqueSuffix(technique)}! ¡GOL!`,
      ({ actor, technique }) => `¡${actor} la clava${techniqueSuffix(technique)}!`,
    ]);
    return narration(text, 'GOL', 'GOLAZO', context.actor, context.technique);
  }
  if (event.type === 'SAVE') {
    const text = choose(saltedEvent, 67, [
      ({ actor, technique }) => `¡PARADÓN de ${actor}${techniqueSuffix(technique)}!`,
      ({ actor, technique }) => `${actor} niega el gol${techniqueSuffix(technique)}.`,
      ({ actor, technique }) => `¡Qué manos de ${actor}${techniqueSuffix(technique)}!`,
      ({ actor, technique }) => `${actor} manda el remate al olvido${techniqueSuffix(technique)}.`,
      ({ actor, technique }) => `¡PARADA enorme de ${actor}${techniqueSuffix(technique)}!`,
      ({ actor, technique }) => `${actor} sostiene a los suyos${techniqueSuffix(technique)}.`,
      ({ actor, technique }) => `¡Increíble respuesta de ${actor}${techniqueSuffix(technique)}!`,
    ]);
    return narration(text, 'PARADÓN', 'PARADA', context.actor, context.technique);
  }
  if (event.type === 'TECHNIQUE') {
    const text = choose(saltedEvent, 71, [
      ({ actor, technique }) => `¡${technique}! ${actor} la desata.`,
      ({ actor, technique }) => `${actor} invoca ${technique}.`,
      ({ actor, technique }) => `¡Atención! ${actor} activa ${technique}.`,
      ({ actor, technique }) => `${technique} entra en escena con ${actor}.`,
      ({ actor, technique }) => `${actor} responde con ${technique}.`,
      ({ actor, technique }) => `¡La energía sube! ${technique}, de ${actor}.`,
      ({ actor, technique }) => `${actor} apuesta por ${technique}.`,
    ]);
    return narration(text, context.actor, context.technique);
  }
  if (event.type === 'FURY') return event.message.includes('entra en')
    ? narration(`¡FASE DE FUROR! ${context.teamName} va con todo.`, 'FASE DE FUROR', context.teamName)
    : narration(`La FASE DE FUROR de ${context.teamName} se apaga.`, 'FASE DE FUROR', context.teamName);
  const text = choose(saltedEvent, 73, [
    ({ actor, target }) => `Cambio: ${target} entra por ${actor}.`,
    ({ actor, target }) => `Se mueve el banquillo: ${target} releva a ${actor}.`,
    ({ actor, target }) => `${target} salta al césped; sale ${actor}.`,
    ({ actor, target }) => `Relevo preparado: ${target} por ${actor}.`,
    ({ actor, target }) => `${actor} deja su sitio a ${target}.`,
  ]);
  return narration(text, context.actor, context.target);
}
